import React, { useEffect, useState } from "react";
import api from "../../api/api";
import { getSessionUsername } from "../../utils/sessionUser";

const Page1 = ({
  selectedTxnTypeId,
  setSelectedTxnTypeId,
  selectedSubTypeId,
  setSelectedSubTypeId,
  docLabel,
  dateLabel,
  docNumber,
  setDocNumber,
  docDate,
  setDocDate,
  addlRefNo,
  setAddlRefNo,
  refDate,
  setRefDate,
  remarks,
  setRemarks,
  onBackToList,
  setPage,
  isPage1NextDisabled,  onGlIdReady,   
}) => {
  const [txnTypes, setTxnTypes] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [loadingTxnTypes, setLoadingTxnTypes] = useState(false);
  const [loadingSubTypes, setLoadingSubTypes] = useState(false);
  const [errorTxnTypes, setErrorTxnTypes] = useState(null);
  const [errorSubTypes, setErrorSubTypes] = useState(null);

  const user = getSessionUsername();

  // Fetch transaction types
  useEffect(() => {
    const fetchTxnTypes = async () => {
      setLoadingTxnTypes(true);
      try {
        const data = await api.get("GetQlTxnTypeAPI", { User: user });
        const mapped =
          data?.Table?.map((item, index) => ({
            id: index + 1,
            name: item.DocTypeName,
          })) || [];
        setTxnTypes(mapped);
      } catch (err) {
        console.error(err);
        setErrorTxnTypes(err.message);
      } finally {
        setLoadingTxnTypes(false);
      }
    };
    fetchTxnTypes();
  }, [user]);

  // Fetch subtypes based on selected txn type
  useEffect(() => {
    if (!selectedTxnTypeId) {
      setSubTypes([]);
      setSelectedSubTypeId("");
      return;
    }

    const selectedType = txnTypes.find(
      (t) => t.id === parseInt(selectedTxnTypeId)
    );
    if (!selectedType) return;

    const fetchSubTypes = async () => {
      setLoadingSubTypes(true);
      try {
        const data = await api.get("GetQlTxnSubTypeAPI", {
          DocTypeName: selectedType.name,
        });
        const mapped =
          data?.Table?.map((item) => ({
            id: item.DocId,
            name: item.SubTypeName,
          })) || [];
        setSubTypes(mapped);
        setSelectedSubTypeId("");
      } catch (err) {
        console.error(err);
        setErrorSubTypes(err.message);
      } finally {
        setLoadingSubTypes(false);
      }
    };
    fetchSubTypes();
  }, [selectedTxnTypeId, txnTypes, setSelectedSubTypeId]);

  const handleNext = async () => {
    if (isPage1NextDisabled) return;

    try {
      const payload = {
        SqlAction: "INSERT",
        GlId: 0,
        DocId: parseInt(selectedSubTypeId),
        SubTypeName:
          subTypes.find((st) => st.id === parseInt(selectedSubTypeId))?.name || "",
        Remarks: remarks,
        DocDate: docDate,
        DocNo: docNumber,
        RefDate: refDate,
        RefNo: addlRefNo,
      };

      const result = await api.post("GlTxnSaveAPI", payload);
      console.log("INSERT Result:", result);

      // API returns nested object — extract Glid
      const apiData = result?.GlTxnSaveAPIjs ?? result;
      const returnedGlId = apiData?.Table?.[0]?.Glid;
      const parsedGlId = parseInt(returnedGlId, 10);

      if (!isNaN(parsedGlId) && parsedGlId > 0) {
      onGlIdReady(parsedGlId); // send GlId back to parent
} else {
        throw new Error("No valid GlId returned from API.");
      }
    } catch (error) {
      console.error("Error saving transaction:", error);
      alert("Failed to save transaction. Check console for details.");
    }
  };


  return (
    <>
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 border-b pb-4">
        Billing Module (Step 1/5)
      </h1>

      {loadingTxnTypes && <p>Loading transaction types...</p>}
      {errorTxnTypes && <p className="text-red-600">{errorTxnTypes}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
        {/* Txn Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Txn Type
          </label>
          <select
            value={selectedTxnTypeId}
            onChange={(e) => setSelectedTxnTypeId(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
          >
            <option value="">-- Select Type --</option>
            {txnTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* Txn SubType */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Txn SubType
          </label>
          {loadingSubTypes ? (
            <p>Loading subtypes...</p>
          ) : errorSubTypes ? (
            <p className="text-red-600">{errorSubTypes}</p>
          ) : (
            <select
              value={selectedSubTypeId}
              onChange={(e) => setSelectedSubTypeId(e.target.value)}
              disabled={!selectedTxnTypeId || subTypes.length === 0}
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-200"
            >
              <option value="">-- Select SubType --</option>
              {subTypes.map((subType) => (
                <option key={subType.id} value={subType.id}>
                  {subType.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Document details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {docLabel}
          </label>
          <input
            type="text"
            value={docNumber}
            onChange={(e) => setDocNumber(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {dateLabel}
          </label>
          <input
            type="date"
            value={docDate}
            onChange={(e) => setDocDate(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Addl Ref. No
          </label>
          <input
            type="text"
            value={addlRefNo}
            onChange={(e) => setAddlRefNo(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ref Date
          </label>
          <input
            type="date"
            value={refDate}
            onChange={(e) => setRefDate(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
          />
        </div>
      </div>

      {/* Remarks */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Remarks
        </label>
        <textarea
          rows="3"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
        ></textarea>
      </div>

      {/* Buttons */}
      <div className="flex justify-between pt-4 border-t mt-4">
        <button
          onClick={onBackToList}
          className="px-6 py-2 bg-gray-600 text-white font-semibold rounded-md shadow-md hover:bg-gray-700"
        >
          Back to List
        </button>
        <button
          onClick={handleNext}
          disabled={isPage1NextDisabled}
          className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-md shadow-md disabled:bg-gray-400"
        >
          Next
        </button>
      </div>
    </>
  );
};

export default Page1;
